<script setup lang="ts">
import { computed, ref, toRaw, watch } from 'vue';
import { trackingSurveyEvent } from '@/api/tracking';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import Datepicker from '@/components/common/datepicker.vue';
import ActionBalloon from '@/components/common/molecules/balloon.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import SearchShareSurveyUsersInput from '@/components/share-survey/search-share-survey-users-input.vue';
import ShareSurveyUserCard from '@/components/share-survey/share-survey-user-card.vue';
import {
  ShareSurveyMode,
  ShareSurveyUser,
  ShareSurveyUserOption,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';
import { formatDate } from '@/utils/formatters';
import { SHARE_SURVEY_PAGE_NAME } from '@/utils/survey/constants';
import { useShareSurveyUserOptions } from '@/utils/swr';

// 有効期限はデフォルトでは1ヶ月後、1日〜1年の範囲で設定できる
const today = dayjs().endOf('day').toDate();
const maxExpiredAt = dayjs().endOf('day').add(1, 'year').toDate();
const defaultExpiredAt = dayjs().endOf('day').add(1, 'month').toDate();
const expiredAt = ref<Date>(defaultExpiredAt);
const { createSnackbar } = useSnackbar();
const { getCurrentPathForTracking } = useCurrentPathForTracking();

type Props = {
  uuid: string;
  owner: ShareSurveyUser;
  currentUserId?: number;
  mode: ShareSurveyMode;
  isSample: boolean;
  initShareSurveyUsers: ShareSurveyUser[];
  initExpiredAt?: Date;
  initIsAllUsersAccessible?: boolean;
};
const props = defineProps<Props>();

type Emits = {
  updateShareSurvey: [
    shareSurveyUsers: ShareSurveyUser[],
    isAllUsersAccessible: boolean,
    expiredAt: Date,
  ];
};
const emit = defineEmits<Emits>();

const open = defineModel<boolean>('open', { default: false });
const isAllUsersAccessible = ref(props.initIsAllUsersAccessible ?? false);
const shareSurveyUsers = ref<ShareSurveyUser[]>([]);
const options = useShareSurveyUserOptions(props.uuid);
const selectableOptions = computed(() => {
  return options.data.value?.filter(
    option =>
      !shareSurveyUsers.value.some(user => user.id === option.id) &&
      option.id !== props.owner.id,
  ) as ShareSurveyUserOption[];
});
const allUsersCount = computed(() => {
  return options.data.value?.length ?? 0;
});

watch(
  open,
  newValue => {
    if (props.mode === 'edit' && newValue) {
      shareSurveyUsers.value = structuredClone(
        toRaw(props.initShareSurveyUsers),
      );
      isAllUsersAccessible.value = props.initIsAllUsersAccessible;
      expiredAt.value = props.initExpiredAt ?? defaultExpiredAt;
    } else {
      shareSurveyUsers.value = [];
      expiredAt.value = defaultExpiredAt;
    }
  },
  { immediate: true },
);

const addShareSurveyUser = (user: ShareSurveyUser) => {
  shareSurveyUsers.value.push(user);
  isAllUsersAccessible.value = false;
};

const selectAllUsersOption = () => {
  shareSurveyUsers.value = [];
  isAllUsersAccessible.value = true;
};

const updateShareSurvey = () => {
  emit(
    'updateShareSurvey',
    shareSurveyUsers.value,
    isAllUsersAccessible.value,
    expiredAt.value,
  );
  closeModal();
};

const updateExpiredAt = (date: Date) => {
  expiredAt.value = date;
};

const removeShareSurveyUser = (user: ShareSurveyUser) => {
  shareSurveyUsers.value = shareSurveyUsers.value.filter(
    item => item.id !== user.id,
  );
};

const removeAllUsersOption = () => {
  isAllUsersAccessible.value = false;
};

const closeModal = () => {
  open.value = false;
};

const copyShareSurveyUrl = () => {
  const url = `${window.location.origin}/#/share_surveys/${props.uuid}`;
  navigator.clipboard.writeText(url).then(() => {
    createSnackbar({
      message: 'シェアスレッドのURLをコピーしました',
      type: 'success',
    });
  });
  trackingSurveyEvent('share_survey_url_copy', {
    page: {
      name: SHARE_SURVEY_PAGE_NAME,
      url: getCurrentPathForTracking(),
    },
    session_id: props.uuid,
  });
};

const title = computed(() => {
  switch (props.mode) {
    case 'create':
      return '共有';
    case 'edit':
      return '共有設定編集';
    case 'view':
      return '共有設定';
    default:
      return '共有設定';
  }
});

// modeがcreateの場合の判定
const buttonDisabled = computed(() => {
  return shareSurveyUsers.value.length === 0 && !isAllUsersAccessible.value;
});

// modeがeditの場合に表示されるメニュー関連
const openMenuUserId = ref<number>();
const openAllUsersOptionMenu = ref(false);
const clickMenu = (userId?: number) => {
  openMenuUserId.value = openMenuUserId.value === userId ? undefined : userId;
};
const isShareSurveyMenuClickOutside = useOutsideClick([
  '.share-survey-users-menu',
]);
watch(isShareSurveyMenuClickOutside, isOutside => {
  if (isOutside) {
    openMenuUserId.value = undefined;
    openAllUsersOptionMenu.value = false;
  }
  isShareSurveyMenuClickOutside.value = false;
});
</script>

<template>
  <DgrDialog
    v-model:open="open"
    :title="title"
    :modal="false"
    :close-on-interact-outside="false"
  >
    <template #description>
      <div class="description">
        <div>共有の有効期限</div>
        <Datepicker
          v-if="mode !== 'view'"
          class="datepicker"
          :date="expiredAt"
          :disabled-until="today"
          :disabled-later-than="maxExpiredAt"
          @dates-changed="updateExpiredAt"
        />
        <div v-else class="date-display">
          {{ formatDate(expiredAt.toISOString()) }}
        </div>
        <div class="search-bar-title">
          このシェアスレッドにアクセスできるユーザー
        </div>
        <SearchShareSurveyUsersInput
          v-if="mode !== 'view'"
          :all-users-count="allUsersCount"
          :options="selectableOptions"
          :is-loading="options.state.value !== 'SUCCESS'"
          :is-all-users-accessible="isAllUsersAccessible"
          @select="addShareSurveyUser"
          @select-all-users-option="selectAllUsersOption"
        ></SearchShareSurveyUsersInput>
        <div class="share-survey-users">
          <div class="item">
            <ShareSurveyUserCard
              :image-url="owner.image_url || ''"
              :user-name="owner.user_name"
              :email="owner.email || ''"
              :is-me="owner.id === currentUserId"
              :use-anews-icon="isSample"
            >
              <template #right>
                <div class="user-role c-text-regular--m">オーナー</div>
              </template>
            </ShareSurveyUserCard>
          </div>
          <div class="all-users-option" v-if="isAllUsersAccessible || isSample">
            <DgrIcon name="building" />
            <div>環境内のすべてのユーザー（{{ allUsersCount }}人）</div>
            <div v-if="mode === 'edit'">
              <button
                class="menu-button c-btn--auto c-btn"
                @click.stop.prevent="
                  openAllUsersOptionMenu = !openAllUsersOptionMenu
                "
              >
                <DgrIcon name="ellipsis-h" size="small" />
              </button>
              <div class="share-survey-users-menu">
                <ActionBalloon
                  position="absolute"
                  :is-show="openAllUsersOptionMenu"
                  balloon-position="right"
                >
                  <div
                    class="menu-item c-title-bold--m"
                    @click="removeAllUsersOption"
                  >
                    <DgrIcon name="trash" size="small" />
                    アクセス権を削除
                  </div>
                </ActionBalloon>
              </div>
            </div>
            <button
              v-else-if="mode === 'create'"
              class="c-btn c-btn--small c-btn--auto c-btnOutline"
              @click="removeAllUsersOption"
            >
              削除
            </button>
            <div v-else />
          </div>
          <template v-if="shareSurveyUsers.length > 0">
            <div class="item" v-for="user in shareSurveyUsers" :key="user.id">
              <ShareSurveyUserCard
                :key="user.id"
                :image-url="user.image_url || ''"
                :user-name="user.user_name"
                :email="user.email || ''"
                :is-me="user.id === currentUserId"
              >
                <template #right>
                  <div v-if="mode === 'edit'">
                    <button
                      class="menu-button c-btn--auto c-btn"
                      @click.stop.prevent="clickMenu(user.id)"
                    >
                      <DgrIcon name="ellipsis-h" size="small" />
                    </button>
                    <div class="share-survey-users-menu">
                      <ActionBalloon
                        position="absolute"
                        :is-show="openMenuUserId === user.id"
                        balloon-position="right"
                      >
                        <div
                          class="menu-item c-title-bold--m"
                          @click="removeShareSurveyUser(user)"
                        >
                          <DgrIcon name="trash" size="small" />
                          アクセス権を削除
                        </div>
                      </ActionBalloon>
                    </div>
                  </div>
                  <button
                    v-else-if="mode === 'create'"
                    class="c-btn c-btn--small c-btn--auto"
                    @click="removeShareSurveyUser(user)"
                  >
                    削除
                  </button>
                  <div v-else />
                </template>
              </ShareSurveyUserCard>
            </div>
          </template>
          <div
            v-else-if="!(isAllUsersAccessible || isSample)"
            class="not-shared-message c-text-regular--m"
          >
            このシェアスレッドはまだ誰とも共有されていません
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="footer">
        <div>
          <button class="copy-button c-btn--small" @click="copyShareSurveyUrl">
            <DgrIcon name="chain" size="small" />
            <div class="c-title-bold--m">シェアスレッドのURLをコピー</div>
          </button>
        </div>
        <template v-if="mode !== 'view'">
          <button class="c-btn c-btn--auto c-btnOutline" @click="closeModal">
            キャンセル
          </button>
          <button
            v-if="mode === 'create'"
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            @click="updateShareSurvey"
            :class="{ disabled: buttonDisabled }"
            :disabled="buttonDisabled"
          >
            共有する
          </button>
          <button
            v-else-if="mode === 'edit'"
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            @click="updateShareSurvey"
          >
            保存
          </button>
        </template>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.description {
  width: 648px;
  height: 437px;
}
.datepicker,
.date-display {
  margin: 8px 0 16px 0;
}
.search-bar-title {
  margin-bottom: 4px;
}
.search-bar {
  margin-bottom: 8px;
}
.share-survey-users {
  .item {
    border-bottom: 1px solid $color-gray400;
  }
}
.footer {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  & > :first-child {
    margin-right: auto;
  }
}
.copy-button {
  display: flex;
  gap: 3px;
  align-items: center;
  width: fit-content;
  border: none;
  background: transparent;
  cursor: pointer;
  &:hover {
    background-color: $color-gray400;
  }
}
.not-shared-message {
  padding: 8px 0;
  color: $color-gray800;
  text-align: center;
}
.all-users-option {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid $color-gray400;
  & > :last-child {
    margin-left: auto;
  }
}
.menu-button {
  padding: 11px;
  border-radius: 4px;
  border: none;
  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }
}
.menu-item {
  width: 132px;
  padding: 5px 16px;
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }
}
</style>
